define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/when",
	"csi",
	"csi/_base/underscore",
	"csi/comm/GetRequest",
	"csi/expander/Widget",
	"csi/locale",
	"csi/table/Preferences",
	"csi/types",
	"csi/xml",
	"csi/xml/Query",
	"Apparel/StructureItemMixin",
	"Calendar/timeline/CustomViewTimelineOptions",
	"Calendar/timeline/TimelineCustomViewOptionsStore",
	"Calendar/timeline/TimelineOptionsTab",
], function(declare, lang, when, csi, _, GetRequest, Expander, locale, Preferences, types, xml, Query, StructureItemMixin, CustomViewTimelineOptions, TimelineCustomViewOptionsStore, TimelineOptionsTab) {

	var _Preferences = declare(Preferences, {
		constructor: function() {
			this.addCustomOption("timelineRange", CustomViewTimelineOptions.TimelineRangeType.RANGE_PRESENT, false, "TimelineRange");
			this.addCustomOption("timelineWeeks", CustomViewTimelineOptions.TimelineWeeksType.THREE, false, "TimelineWeeks");
		},

		_timelineRangeLoad: function(value, options, mapKey) {
			// summary: option was previously stored as string, so convert to number
			return Number(value);
		},

		_timelineWeeksLoad: function(value, options, mapKey) {
			// summary: option was previously stored as string, so convert to number
			return Number(value);
		}
	});

	return declare([Expander, StructureItemMixin], {
		customViewOptionsComponent: TimelineOptionsTab,
		customViewOptionsStore: TimelineCustomViewOptionsStore,

		prefClass: _Preferences,

		// Object: map of urls whose activities have been queried
		timelineActivities: null,

		// Number: number of weeks offset from present week
		timelineOffset: 0,

		// Number: indicates how timeline week range is determined relative to present date:
		//	RANGE_PAST - present week is at end of range
		//	RANGE_PRESENT - present week is in middle of range
		//	RANGE_FUTURE - present week is at start of range
		//timelineRange: RANGE_PRESENT,

		// Number: number of weeks to display in timeline
		//timelineWeeks: 0,

		// Date: first day of present week
		timelinePresentDate: null,

		// Date: calculated dates indicating the start and end of the timeline
		timelineStartDate: null,
		timelineEndDate: null,

		constructor: function() {
			// summary: initialize present date
			this.timelinePresentDate = types.getWeekStartDate();
		},

		onViewPreferences: function() {
			// summary: apply custom options and reset offset
			this.inherited(arguments);
			this.set("timelineOffset", 0);
		},

		postActionTimespan: function(/*Controller*/ac) {
			// summary: refresh activity data when changing number of weeks to display
			this.timelineActivities = null;
			this._resetLevels();
			return this.redraw();
		},

		/**
		 * @override
		 * Return dropdown options for Timeline options
		 */
		getCustomViewDropDownOptions() {
			const options = this.inherited(arguments);

			// Time Range dropdown options
			const timelineRangeOptions = [
				{id: CustomViewTimelineOptions.TimelineRangeType.RANGE_PAST, name: locale.resource("TimelineRangePast")},
				{id: CustomViewTimelineOptions.TimelineRangeType.RANGE_PRESENT, name: locale.resource("TimelineRangePresent")},
				{id: CustomViewTimelineOptions.TimelineRangeType.RANGE_FUTURE, name: locale.resource("TimelineRangeFuture")}
			];

			return {
				...options,
				timelineRangeOptions,
				timelineWeeksOptions: CustomViewTimelineOptions.TimelineWeeksOptions,
			}
		},

		_buildViewResults: function(/*QueryResult*/qrParent, /*DOMNode*/row, /*DOMNode*/rowNext, /*QueryResults*/results, /*Boolean*/bRefresh) {
			// summary: query for activities before generating rows
			var urls = _.map(results, xml.nodeGetURL);

			return when(this._queryActivities(urls)).then(lang.hitch(this, Expander.prototype._buildViewResults, qrParent, row, rowNext, results, bRefresh));
		},

		_queryActivities: function(/*String[]*/urls, /*Boolean*/bForce) {
			// summary: query for activities of given nodes
			var a = this.timelineActivities = this.timelineActivities || {};

			if (!bForce) {
				urls = _.filter(urls, function(url) {return !a[url];});
			}

			if (!urls.length) return null;

			var request = new GetRequest("Calendar", "GetActivitiesForTimePeriod", {
				BeginTime: types.getDateTime(this.timelineStartDate),
				EndTime: types.getDateTime(this.timelineEndDate)
			});

			_.each(urls, function(url) {
				request.addParameter("URL", url, true);

				// mark that url has been queried - even if no activities are returned later
				a[url] = [];
			});

			return request.get().then(lang.hitch(this, function(response) {
				this.cacheView.cacheResponse(response);

				// store activities per row
				_.each(response.getNodes(), function(node) {
					csi.objectArrayAddValue(a, node.CalendarContainer, node);
				});

				// sort activities by date
				_.each(_.keys(a), function(url) {
					a[url] = csi.sort(a[url], function(node) {
						return node.CompletionDate || node.ExpectedEndDate;
					});
				});
			}));
		},

		_redrawNode: function(/*DOMNode*/element) {
			// summary: reset cache for node's activities when refreshing
			var url = this.getRefNodeUrl(this.getRefNode(element));

			when(this._queryActivities([url], true), lang.hitch(this, function() {
				Expander.prototype._redrawNode.call(this, element);
			}));
		},

		_refreshQueries: function() {
			// summary: set appropriate queries for view
			var node = this.getViewNode();
			if (!node) return;

			var q = new Query();
			var r = this.relRoot;

			if (node.$Type == "Style") {
				r = this.defView.m_relationships[1];

				// only query for active Colorways if Style is itself active; otherwise show all Colorways
				if (node.Active) {
					q.Attr("Active", xml.OP_EQUALS, true);
				}
			} else {
				r.m_addPaths = null;

				// query for active Styles with Calendars under the current hierarchy node
				var styleRelation = this.TypeRelations[node.$Type];
				if (styleRelation) {
					q.RefAttr(styleRelation, xml.OP_EQUALS, node.$URL);
				}

				q.Attr("Active", xml.OP_EQUALS, true);
			}

			q.Attr2("Calendar", xml.OP_NOT_EQUALS, {"Dimension": 0});
			r.addQuery(q, true);
		},

		_resetStores: function() {
			// summary: reset timeline data
			this.timelineActivities = null;
			this.inherited(arguments);
		},

		_resolveView: function() {
			// summary: add dependency paths for Calendar information
			this._refreshQueries();
			this.inherited(arguments);
		},

		_setTimelineOffsetAttr: function(/*Number*/n) {
			this._set("timelineOffset", n);
			this._updateTimeline();
		},

		_setTimelineRangeAttr: function(/*Number*/n) {
			this._set("timelineRange", n);
			this._updateTimeline();
		},

		_setTimelineWeeksAttr: function(/*Number*/n) {
			this._set("timelineWeeks", n);
			this._updateTimeline();
		},

		_updateTimeline: function() {
			// summary: calculate start and end dates of timeline
			var nWeeksToStart;
			var nWeeksToEnd;

			// when view is first loading, it is possible timelineRange is set from custom view before timelineWeeks is ever set. In that case,
			// ignore and wait for timelineWeeks to be set.
			if (!this.timelineWeeks) {
				return;
			}

			if (this.timelineRange == CustomViewTimelineOptions.TimelineRangeType.RANGE_PAST) {
				nWeeksToStart = -this.timelineWeeks + 1;
				nWeeksToEnd = 1;
			} else if (this.timelineRange == CustomViewTimelineOptions.TimelineRangeType.RANGE_PRESENT) {
				nWeeksToStart = -(this.timelineWeeks - 1) / 2;
				nWeeksToEnd = 1 - nWeeksToStart;
			} else {
				nWeeksToStart = 0;
				nWeeksToEnd = this.timelineWeeks;
			}

			// reset end date first so that listeners will not do anything until both start date and end date are set
			this.set("timelineEndDate", null);
			this.set("timelineStartDate", types.addDaysToDate(this.timelinePresentDate, (nWeeksToStart + this.timelineOffset) * 7, true));
			this.set("timelineEndDate", types.addDaysToDate(this.timelinePresentDate, (nWeeksToEnd + this.timelineOffset) * 7, true));
		}
	});
});